/**
 * Start screen, pause menu and end-game stats screen (DOM builders used by HUD).
 */
import { G } from './ctx';
import { formatMoney } from './util';
import { CONTROLS_LIST, controlsHtml } from './hud_icons';
import type { IHUD } from './types';

export const SAVE_KEY = 'gtb-save-v1';

type Quality = IHUD['settings']['quality'];

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, html?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (html !== undefined) e.innerHTML = html;
  return e;
}

function qualitySeg(settings: IHUD['settings'], onChange?: () => void): HTMLElement {
  const seg = el('div', 'gs-seg');
  const opts: Quality[] = ['low', 'medium', 'high'];
  const btns = opts.map((q) => {
    const b = el('button', undefined, q.toUpperCase());
    b.type = 'button';
    if (settings.quality === q) b.classList.add('on');
    b.onclick = () => {
      settings.quality = q;
      btns.forEach((x, i) => x.classList.toggle('on', opts[i] === q));
      onChange?.();
    };
    seg.appendChild(b);
    return b;
  });
  return seg;
}

export function hasSave(): boolean {
  try {
    return !!localStorage.getItem(SAVE_KEY) && !new URLSearchParams(location.search).has('fresh');
  } catch {
    return false;
  }
}

export function resetSave(): void {
  try {
    localStorage.removeItem(SAVE_KEY);
  } catch {
    /* ignore */
  }
  location.href = location.pathname + '?fresh';
}

// ------------------------------------------------------------------ start ----

export function createStartScreen(settings: IHUD['settings'], isMobile: boolean, isTouch: boolean): { el: HTMLElement; done: Promise<void> } {
  const root = el('div', 'gs');
  const save = hasSave();
  const logo = el('div', 'gs-logo');
  const title = 'GRAND THEFT';
  logo.innerHTML = `<div class="gs-title fill">${title}</div><div class="gs-title stroke" aria-hidden="true">${title}</div>`;
  const sub = el('div', 'gs-sub', 'BUDAPEST');
  const tag = el('div', 'gs-tag', 'Steal it. Drive it. Lose them.');
  const row = el('div', 'gs-row');
  const play = el('button', 'gs-btn primary', save ? 'Continue' : 'Play');
  play.id = 'play';
  play.type = 'button';
  row.appendChild(play);
  if (save) {
    const fresh = el('button', 'gs-btn small', 'New game');
    fresh.type = 'button';
    fresh.onclick = () => resetSave();
    row.appendChild(fresh);
  }
  const controls = el('div', 'gs-controls', controlsHtml(CONTROLS_LIST));
  const opts = el('div', 'gs-opts');
  opts.appendChild(el('span', undefined, 'Quality'));
  opts.appendChild(qualitySeg(settings));
  root.append(logo, sub, tag, row);
  if (!isTouch) root.appendChild(controls);
  root.appendChild(opts);
  if (isMobile || isTouch) root.appendChild(el('div', 'gs-note', 'Use the on-screen controls: left stick to move, buttons on the right, drag the right side to look around.'));
  else root.appendChild(el('div', 'gs-note', 'Click the game to lock the mouse. Press Esc to pause.'));
  root.appendChild(el('div', 'gs-foot', 'GRAND THEFT BUDAPEST · browser edition'));

  const done = new Promise<void>((resolve) => {
    let fired = false;
    const go = () => {
      if (fired) return;
      fired = true;
      root.classList.add('out');
      setTimeout(() => root.remove(), 800);
      resolve();
    };
    play.addEventListener('click', go);
    play.addEventListener('touchend', (e) => {
      e.preventDefault();
      go();
    });
  });
  return { el: root, done };
}

// ------------------------------------------------------------------ pause ----

type Tab = 'settings' | 'controls' | 'stats';

export class PauseMenu {
  readonly el: HTMLElement;
  private body: HTMLElement;
  private tabs: Record<Tab, HTMLButtonElement>;
  private tab: Tab = 'settings';
  private statsEl: HTMLElement | null = null;
  private lastStats = '';

  constructor(
    private settings: IHUD['settings'],
    private onResume: () => void,
  ) {
    this.el = el('div', 'gp');
    const panel = el('div', 'gp-panel');
    const head = el('div', 'gp-head');
    head.appendChild(el('div', 'gp-title', 'PAUSED'));
    head.appendChild(el('div', 'gp-sub', 'GRAND THEFT BUDAPEST'));
    const tabs = el('div', 'gp-tabs');
    const resume = el('button', 'gp-tab resume', 'Resume');
    resume.type = 'button';
    resume.onclick = () => this.onResume();
    tabs.appendChild(resume);
    const mk = (id: Tab, label: string) => {
      const b = el('button', 'gp-tab', label);
      b.type = 'button';
      b.onclick = () => this.show(id);
      tabs.appendChild(b);
      return b;
    };
    this.tabs = { settings: mk('settings', 'Settings'), controls: mk('controls', 'Controls'), stats: mk('stats', 'Stats') };
    this.body = el('div', 'gp-body');
    const foot = el('div', 'gp-foot');
    const reset = el('button', 'gp-btn danger', 'Reset save');
    reset.type = 'button';
    let armed = false;
    reset.onclick = () => {
      if (!armed) {
        armed = true;
        reset.textContent = 'Confirm reset';
        setTimeout(() => {
          armed = false;
          reset.textContent = 'Reset save';
        }, 3000);
        return;
      }
      resetSave();
    };
    const res2 = el('button', 'gp-btn primary', 'Resume');
    res2.type = 'button';
    res2.onclick = () => this.onResume();
    foot.append(reset, res2);
    panel.append(head, tabs, this.body, foot);
    this.el.appendChild(panel);
    // click on the backdrop (outside the panel) resumes
    this.el.addEventListener('click', (e) => {
      if (e.target === this.el) this.onResume();
    });
  }

  open(): void {
    this.el.classList.add('on');
    this.show(this.tab);
  }

  close(): void {
    this.el.classList.remove('on');
  }

  /** refresh live values (stats) while open */
  tick(): void {
    if (this.tab === 'stats' && this.statsEl) this.renderStats(this.statsEl);
  }

  private show(tab: Tab): void {
    this.tab = tab;
    (Object.keys(this.tabs) as Tab[]).forEach((k) => this.tabs[k].classList.toggle('on', k === tab));
    this.body.innerHTML = '';
    this.statsEl = null;
    if (tab === 'settings') this.body.appendChild(this.buildSettings());
    else if (tab === 'controls') this.body.appendChild(el('div', 'gp-ctl', controlsHtml(CONTROLS_LIST)));
    else {
      this.statsEl = el('div', 'gp-stats');
      this.lastStats = '';
      this.renderStats(this.statsEl);
      this.body.appendChild(this.statsEl);
    }
  }

  private row(label: string, control: HTMLElement, hint?: string): HTMLElement {
    const r = el('div', 'gp-row');
    const l = el('label', undefined, label + (hint ? `<span class="hint">${hint}</span>` : ''));
    r.append(l, control);
    return r;
  }

  private toggle(get: () => boolean, set: (v: boolean) => void): HTMLElement {
    const b = el('button', 'gp-sw');
    b.type = 'button';
    b.setAttribute('role', 'switch');
    const sync = () => {
      b.classList.toggle('on', get());
      b.setAttribute('aria-checked', String(get()));
    };
    sync();
    b.onclick = () => {
      set(!get());
      sync();
    };
    return b;
  }

  private range(get: () => number, set: (v: number) => void): HTMLElement {
    const w = el('div', 'gp-range');
    const i = el('input');
    i.type = 'range';
    i.min = '0';
    i.max = '100';
    i.step = '1';
    i.value = String(Math.round(get() * 100));
    const o = el('output', undefined, i.value + '%');
    i.oninput = () => {
      set(Number(i.value) / 100);
      o.textContent = i.value + '%';
    };
    w.append(i, o);
    return w;
  }

  private buildSettings(): HTMLElement {
    const s = this.settings;
    const box = el('div');
    box.appendChild(this.row('Quality', qualitySeg(s), 'render scale, shadows, bloom'));
    box.appendChild(
      this.row(
        'Bloom',
        this.toggle(
          () => s.bloom,
          (v) => (s.bloom = v),
        ),
      ),
    );
    box.appendChild(
      this.row(
        'Shadows',
        this.toggle(
          () => s.shadows,
          (v) => (s.shadows = v),
        ),
      ),
    );
    box.appendChild(
      this.row(
        'Invert look Y',
        this.toggle(
          () => s.invertY,
          (v) => (s.invertY = v),
        ),
      ),
    );
    const vol = G.audio?.volumes;
    if (vol) {
      box.appendChild(
        this.row(
          'Master volume',
          this.range(
            () => vol.master,
            (v) => (vol.master = v),
          ),
        ),
      );
      box.appendChild(
        this.row(
          'Sound effects',
          this.range(
            () => vol.sfx,
            (v) => (vol.sfx = v),
          ),
        ),
      );
      box.appendChild(
        this.row(
          'Music / radio',
          this.range(
            () => vol.music,
            (v) => (vol.music = v),
          ),
        ),
      );
    }
    return box;
  }

  private renderStats(target: HTMLElement): void {
    const st = G.stats;
    const items = statItems({
      Money: formatMoney(st.money),
      Kills: st.kills,
      'Cars stolen': st.carsStolen,
      'Distance driven': (st.distanceDriven / 1000).toFixed(1) + ' km',
      'Distance walked': (st.distanceWalked / 1000).toFixed(1) + ' km',
      'Time played': fmtDuration(st.timePlayed),
      Deaths: st.deaths,
      Busts: st.busts,
      'Max wanted': '★'.repeat(st.maxWanted) || 'none',
      'Missions done': G.missions?.completed?.length ?? 0,
    });
    if (items === this.lastStats) return;
    this.lastStats = items;
    target.innerHTML = items;
  }
}

export function fmtDuration(sec: number): string {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  return h > 0 ? `${h}h ${m.toString().padStart(2, '0')}m` : `${m}m ${s.toString().padStart(2, '0')}s`;
}

export function statItems(stats: Record<string, string | number>): string {
  return Object.entries(stats)
    .map(([k, v]) => `<div class="gp-stat"><b>${typeof v === 'number' ? v.toLocaleString('en-US') : v}</b><span>${k}</span></div>`)
    .join('');
}

// ------------------------------------------------------------------ stats ----

export function createStatsScreen(stats: Record<string, string | number>, onContinue: () => void): HTMLElement {
  const root = el('div', 'gp on');
  const panel = el('div', 'gp-panel');
  const head = el('div', 'gp-head');
  head.appendChild(el('div', 'gp-title', 'YOUR RECORD'));
  head.appendChild(el('div', 'gp-sub', 'GRAND THEFT BUDAPEST'));
  const body = el('div', 'gp-body');
  body.appendChild(el('div', 'gp-stats', statItems(stats)));
  const foot = el('div', 'gp-foot');
  const btn = el('button', 'gp-btn primary', 'Continue');
  btn.type = 'button';
  btn.onclick = () => {
    root.remove();
    onContinue();
  };
  foot.appendChild(btn);
  panel.append(head, body, foot);
  root.appendChild(panel);
  return root;
}
